import { useCallback, useMemo, useState } from "react";

import _ from "lodash";
import moment from "moment";

import { queryIsLoading, useGetPracticeMineralFertilizers, useGetPracticeOrganicFertilizerApplications, useGetPracticeOrganicFertilizers } from "utils";

import { ENUM_FERTILIZER_UNITS, FertilizerApplication, FertilizerType, PracticeFCPO } from "models";

import { Form } from "components";

import { BLANK_FERTILIZER_APPLICATION } from "pages/app/all-field-view/constants/fertilizerForm";
import { determinePracticeSettings } from "pages/app/all-field-view/utils/AllFieldsUtils.ts";
import { PracticeConfigForBulk } from "pages/app/all-field-view/utils/BulkDataEntryUtils.ts";

export const useFertilizerForm = (
  chosenFertilizerType: FertilizerType,
  existingFertilizers: PracticeFCPO[],
  disabled: boolean,
  practiceConfigForBulk?: PracticeConfigForBulk
) => {
  const form = Form.useFormInstance();
  const mineralFertilizerPracticesQuery = useGetPracticeMineralFertilizers({ enabled: true });
  const organicFertilizerPracticesQuery = useGetPracticeOrganicFertilizers({ enabled: true });
  const fertilizerApplicationPracticesQuery = useGetPracticeOrganicFertilizerApplications({ enabled: true });

  const { defaultToggleState, existingPractices, toggleDisabled, hasMixedValues } = useMemo(
    () =>
      determinePracticeSettings({
        practiceConfigForBulk: practiceConfigForBulk,
        practices: existingFertilizers,
        disabled,
      }),
    [practiceConfigForBulk, existingFertilizers, disabled]
  );

  const [isFertilizerTypeApplied, setIsFertilizerTypeApplied] = useState<boolean | null>(defaultToggleState);
  const [fertilizerApplications, setFertilizerApplications] = useState(() => existingPractices);

  const handleFertilizerTypeChange = useCallback(
    (value: boolean) => {
      // Read all form values synchronously, before any state update triggers a re-render
      // that would unmount the OrganicOrMineralFertilizerForm components and clear the store.
      const allValues = value === false ? (form.getFieldsValue(true) as Record<string, Record<string, unknown>>) : null;
      setFertilizerApplications((prev) => {
        if (value === false && prev.length > 0) {
          // Snapshot current Ant Form field values so the form restores correctly if the user toggles back to Yes.
          return prev.map((app, index) => {
            const fields = allValues![`${chosenFertilizerType}-${index}`] ?? {};
            const dateRange = fields.dateRange as [moment.Moment | null, moment.Moment | null] | undefined;
            return {
              ...app,
              ...(fields.assignedPracticeId !== undefined && { assignedPracticeId: fields.assignedPracticeId as string }),
              ...(fields.selfProduced !== undefined && { selfProduced: fields.selfProduced as boolean | null }),
              ...(fields.amount !== undefined && { amount: fields.amount as number }),
              ...(fields.amountUnit !== undefined && { amountUnit: fields.amountUnit as ENUM_FERTILIZER_UNITS }),
              ...(fields.nValue !== undefined && { nValue: fields.nValue as number }),
              ...(fields.applicationType !== undefined && { applicationType: fields.applicationType as string }),
              ...(fields.sanityNoteNitrogen !== undefined && { sanityNoteNitrogen: fields.sanityNoteNitrogen as FertilizerApplication["sanityNoteNitrogen"] }),
              ...(dateRange?.[0]?.isValid() && { startDate: dateRange[0]!.format("YYYY-MM-DD") }),
              ...(dateRange?.[1]?.isValid() && { endDate: dateRange[1]!.format("YYYY-MM-DD") }),
            };
          });
        }
        // if the first application is blank and the user chooses "Yes", add a new fertilizer application.
        if (value === true && prev.length === 0) {
          return [BLANK_FERTILIZER_APPLICATION(chosenFertilizerType)];
        }
        return prev;
      });
      setIsFertilizerTypeApplied(value);
    },
    [chosenFertilizerType, form]
  );

  const handleRemoveFertilizerApplication = (index: number) => {
    setFertilizerApplications((prev) => {
      // if the last application is removed, set the state to null so as to close the radio group.
      // Doing this because React batch state updates and the state is not updated until the next rendering cycle.

      const updated = [...prev];
      updated.splice(index, 1);

      // update form
      const values = form.getFieldsValue(true);
      for (let i = index; i < updated.length; i += 1) {
        values[`${chosenFertilizerType}-${i}`] = values[`${chosenFertilizerType}-${i + 1}`];
      }
      delete values[`${chosenFertilizerType}-${updated.length}`];
      form.setFieldsValue(values);

      // update UI
      if (updated.length === 0) {
        form.setFieldValue(`is${_.upperFirst(chosenFertilizerType)}Applied`, null);
        setIsFertilizerTypeApplied(null);
      }
      return updated;
    });
  };

  const addNewFertilizerApplication = useCallback(() => {
    setFertilizerApplications((prev) => [...prev, BLANK_FERTILIZER_APPLICATION(chosenFertilizerType)]);
  }, [chosenFertilizerType]);

  const updateFertilizerApplication = useCallback((index: number, values: Partial<FertilizerApplication>) => {
    setFertilizerApplications((prev) => prev.map((app, i) => (i === index ? { ...app, ...values } : app)));
  }, []);

  return {
    isFertilizerTypeApplied,
    fertilizerApplications,
    handleFertilizerTypeChange,
    handleRemoveFertilizerApplication,
    addNewFertilizerApplication,
    updateFertilizerApplication,
    toggleDisabled,
    hasMixedValues,
    fertilizers: {
      applications: fertilizerApplicationPracticesQuery.data ?? [],
      mineral: mineralFertilizerPracticesQuery.data ?? [],
      organic: organicFertilizerPracticesQuery.data ?? [],
    },
    isFertilizerLoading: [mineralFertilizerPracticesQuery, organicFertilizerPracticesQuery, fertilizerApplicationPracticesQuery].some(queryIsLoading),
  };
};
